// src/components/customers/CustomersList.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const CustomersList = ({ onTabChange }) => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);

  const loadMoreRef = useRef();

  // Memoized customers array
  const safeCustomers = useMemo(() => 
    Array.isArray(customers) ? customers : [],
    [customers]
  );

  const isEmpty = useMemo(() =>
    safeCustomers.length === 0 && !isLoading && !isLoadingMore,
    [safeCustomers, isLoading, isLoadingMore]
  );

  // Pagination data
  const {
    current_page = 1,
    total_pages = 1,
    total_customers = 0,
    customers_per_page = 10,
    has_previous = false,
    has_next = false
  } = pagination;

  // Infinite scroll setup
  useEffect(() => {
    if (!has_next || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [has_next, isLoading, isLoadingMore]);

  // Filter customers based on search term
  useEffect(() => {
    const filtered = safeCustomers.filter(customer => 
      customer.recipient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.recipient_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.recipient_phone.includes(searchTerm) ||
      (customer.company_name && customer.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredCustomers(filtered);
  }, [searchTerm, safeCustomers]);

  // Fetch customers with pagination
  const fetchCustomers = useCallback(async (page = currentPage, limit = currentLimit, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const response = await api.post(`${API_CONFIG.CUSTOMERS.GET_CUSTOMERS_LIST}?page=${page}&limit=${limit}`);
      const data = response.data;
      
      if (data.success) {
        if (append) {
          // Append new customers to existing ones
          setCustomers(prev => [...prev, ...(data.recipients || [])]);
        } else {
          // Replace customers for new page
          setCustomers(data.recipients || []);
        }
        
        setPagination(data.pagination || {});
        
        if (data.recipients && data.recipients.length === 0 && !append) {
          showToast('No customers found', 'info');
        }
      } else {
        showToast(data.error || 'Failed to fetch customers', 'error');
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      showToast('An error occurred while fetching customers', 'error');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [currentPage, currentLimit]);

  // Handle loading more customers
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !has_next) return;
    
    setIsLoadingMore(true);
    try {
      await fetchCustomers(current_page + 1, currentLimit, true);
    } catch (error) {
      console.error('Error loading more customers:', error);
      showToast('Failed to load more customers', 'error');
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, has_next, current_page, currentLimit, fetchCustomers]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Handle limit change
  const handleLimitChange = useCallback((newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  }, []);

  // Initial data load
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Reload when page or limit changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchCustomers(currentPage, currentLimit, false);
    }
  }, [currentPage, currentLimit, fetchCustomers]);

  // Skeleton Loader Components
  const SkeletonCustomerRow = () => (
    <tr className="animate-pulse">
      <td className="px-3 py-3 whitespace-nowrap">
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full"></div>
          <div className="ml-2 min-w-0 flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 whitespace-nowrap hidden sm:table-cell">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </td>
      <td className="px-3 py-3 whitespace-nowrap hidden md:table-cell">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <div className="flex space-x-1">
          <div className="h-8 bg-gray-200 rounded w-16"></div>
          <div className="h-8 bg-gray-200 rounded w-16"></div>
        </div>
      </td>
    </tr>
  );

  // Pagination Controls Component
  const PaginationControls = () => {
    if (total_pages <= 1) return null;

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 px-4 py-4 border-t border-gray-200 bg-white">
        {/* Items per page selector */}
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-700">Show:</span>
          <select
            value={customers_per_page}
            onChange={(e) => handleLimitChange(parseInt(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-xs text-gray-700">per page</span>
        </div>

        {/* Page info */}
        <div className="text-xs text-gray-700">
          Showing {(current_page - 1) * customers_per_page + 1} to{' '}
          {Math.min(current_page * customers_per_page, total_customers)} of{' '}
          {total_customers.toLocaleString()} customers
        </div>

        {/* Page navigation */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(current_page - 1)}
            disabled={!has_previous || isLoading}
            className="px-2 py-1 border border-gray-300 rounded-md text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors"
          >
            Previous
          </button>
          
          {/* Page numbers */}
          {Array.from({ length: Math.min(5, total_pages) }, (_, i) => {
            let pageNum;
            if (total_pages <= 5) {
              pageNum = i + 1;
            } else if (current_page <= 3) {
              pageNum = i + 1;
            } else if (current_page >= total_pages - 2) {
              pageNum = total_pages - 4 + i;
            } else {
              pageNum = current_page - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                disabled={isLoading}
                className={`px-2 py-1 border text-xs font-medium transition-colors ${
                  current_page === pageNum
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 text-gray-700 hover:bg-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(current_page + 1)}
            disabled={!has_next || isLoading}
            className="px-2 py-1 border border-gray-300 rounded-md text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Load More Component for Infinite Scroll
  const LoadMoreTrigger = () => {
    if (!has_next) return null;

    return (
      <div ref={loadMoreRef} className="py-4 text-center">
        {isLoadingMore ? (
          <div className="flex items-center justify-center space-x-2 text-gray-600">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-xs">Loading more customers...</span>
          </div>
        ) : (
          <button
            onClick={handleLoadMore}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
          >
            Load More
          </button>
        )}
      </div>
    );
  };

  const handleDelete = async (id, name) => {
    // Your existing delete confirmation logic remains the same
    const showDeleteConfirmation = () => {
      return new Promise((resolve) => {
        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
        
        // Create dialog
        const dialog = document.createElement('div');
        dialog.className = 'bg-white rounded-xl shadow-lg max-w-md w-full mx-auto transform transition-all';
        
        dialog.innerHTML = `
          <div class="p-6">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900" style="font-size: 16px;">Delete Customer</h3>
                <p class="text-gray-600 mt-1" style="font-size: 14px;">This action cannot be undone</p>
              </div>
            </div>
            
            <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p class="text-red-800 font-medium" style="font-size: 14px;">You're about to delete:</p>
              <p class="text-red-700 mt-1" style="font-size: 14px;">"${name}"</p>
              <p class="text-red-600 mt-2" style="font-size: 13px;">All customer data will be permanently removed from the system.</p>
            </div>
            
            <div class="flex flex-col sm:flex-row gap-3 justify-end">
              <button 
                id="cancelDelete" 
                class="px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-white transition-colors flex-1 sm:flex-none"
                style="font-size: 14px;"
              >
                Cancel
              </button>
              <button 
                id="confirmDelete" 
                class="px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors flex-1 sm:flex-none"
                style="font-size: 14px;"
              >
                Delete Customer
              </button>
            </div>
          </div>
        `;
        
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);
        
        // Add event listeners
        const cancelBtn = document.getElementById('cancelDelete');
        const confirmBtn = document.getElementById('confirmDelete');
        
        const cleanup = () => {
          if (document.body.contains(overlay)) {
            document.body.removeChild(overlay);
          }
        };
        
        cancelBtn.onclick = () => {
          cleanup();
          resolve(false);
        };
        
        confirmBtn.onclick = () => {
          cleanup();
          resolve(true);
        };
        
        // Close on overlay click
        overlay.onclick = (e) => {
          if (e.target === overlay) {
            cleanup();
            resolve(false);
          }
        };
        
        // Close on Escape key
        const handleEscape = (e) => {
          if (e.key === 'Escape') {
            cleanup();
            document.removeEventListener('keydown', handleEscape);
            resolve(false);
          }
        };
        
        document.addEventListener('keydown', handleEscape);
        
        // Focus the cancel button for accessibility
        cancelBtn.focus();
      });
    };

    const shouldDelete = await showDeleteConfirmation();
    if (!shouldDelete) {
      return;
    }

    try {
      const response = await api.delete(`${API_CONFIG.CUSTOMERS.DELETE_CUSTOMER}/${id}`);
      
      if (response.data.success) {
        showToast('Customer deleted successfully', 'success');
        // Reload customers after deletion
        fetchCustomers(1, currentLimit, false);
      } else {
        showToast(response.data.message || 'Failed to delete customer', 'error');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      showToast('An error occurred while deleting customer', 'error');
    }
  };

  const displayCustomers = searchTerm ? filteredCustomers : safeCustomers;

  if (isLoading && safeCustomers.length === 0) {
    return (
      <div className="p-3 pb-[90px]">
        {/* Header Section */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="min-w-0 flex-1">
              <h5 className="text-sm font-semibold  truncate" style={{ fontSize: '14pt' }}>
                Customer Management
              </h5>
              <p className="text-xs text-gray-600 mt-1 truncate" style={{ fontSize: '11pt' }}>
                Loading customers...
              </p>
            </div>
          </div>
        </div>

        {/* Loading skeleton for table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Contact
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Company
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: 5 }, (_, i) => (
                  <SkeletonCustomerRow key={i} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 pb-[90px]">
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div className="min-w-0 flex-1">
            <h5 className="text-sm font-semibold  truncate" style={{ fontSize: '14pt' }}>
              Customer Management
            </h5>
            <p className="text-xs text-gray-600 mt-1 truncate" style={{ fontSize: '11pt' }}>
              {isLoading ? 'Loading customers...' : `Showing ${safeCustomers.length} of ${total_customers.toLocaleString()} customers`}
            </p>
          </div>
          <nav className="flex items-center">
            <ol className="flex items-center space-x-1 text-xs">
              <li>
                <Link to="/" className="text-blue-600 hover:text-blue-800 transition-colors truncate" style={{ fontSize: '11pt' }}>
                  Dashboard
                </Link>
              </li>
              <li className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <span className="text-gray-600 truncate" style={{ fontSize: '11pt' }}>Customers</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 mb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-xs"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ fontSize: '11pt' }}
            />
          </div>
    
          <button 
            className="flex items-center px-3 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors w-full md:w-auto justify-center text-xs"
            onClick={() => onTabChange('create-customer')}
            style={{ fontSize: '11pt' }}
          >
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Add Customer
          </button>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isEmpty ? (
          <div className="text-center py-8">
            {searchTerm ? (
              <>
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h3 className="mt-3 text-sm font-medium text-gray-900" style={{ fontSize: '13pt' }}>No customers found</h3>
                <p className="mt-1 text-xs text-gray-600" style={{ fontSize: '11pt' }}>No customers match your search criteria.</p>
                <button 
                  className="mt-3 px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  onClick={() => setSearchTerm('')}
                  style={{ fontSize: '11pt' }}
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                <h3 className="mt-3 text-sm font-medium text-gray-900" style={{ fontSize: '13pt' }}>No customers yet</h3>
                <p className="mt-1 text-xs text-gray-600" style={{ fontSize: '11pt' }}>Get started by adding your first customer.</p>
                <button 
                  onClick={() => onTabChange('create-customer')}
                  className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent rounded shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  style={{ fontSize: '11pt' }}
                >
                  Add Your First Customer
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Contact
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Company
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayCustomers.map(customer => (
                    <tr key={customer.id} className="hover:bg-gray-100 transition-colors duration-150">
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center min-w-0">
                          <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="font-medium text-blue-800 text-xs">
                              {customer.recipient_name.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-2 min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate" style={{ fontSize: '12pt' }}>
                              {customer.recipient_name}
                            </div>
                            <div className="text-xs text-gray-500 sm:hidden truncate">
                              {customer.recipient_email || customer.recipient_phone || '-'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-sm text-gray-900 truncate" style={{ fontSize: '12pt' }}>
                          {customer.recipient_email || '-'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {customer.recipient_phone || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap hidden md:table-cell">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100  truncate max-w-24">
                          {customer.company_name || 'No company'}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-1">
                          <Link 
                            to={`/customers/edit/${customer.id}`} 
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            style={{ fontSize: '10pt' }}
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                            </svg>
                            Edit
                          </Link>
                          <button 
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                            onClick={() => handleDelete(customer.id, customer.recipient_name)}
                            style={{ fontSize: '10pt' }}
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Additional skeleton rows when loading more */}
                  {isLoadingMore && Array.from({ length: 3 }, (_, i) => (
                    <SkeletonCustomerRow key={`skeleton-${i}`} />
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load More Trigger for Infinite Scroll */}
            <LoadMoreTrigger />
          </>
        )}
      </div>

      {/* Pagination Controls */}
      <PaginationControls />

      {/* Stats Section */}
      {displayCustomers.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-1 bg-blue-100 rounded-lg">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <div className="ml-2">
                <p className="text-xs font-medium text-gray-700">Total Customers</p>
                <p className="text-lg font-bold text-gray-900">{total_customers.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersList;